// Copy and photos for /3d-printing-nyc/ (PrintingServicePage variant="nyc").
// Same published facts as /services/3d-printing/ (printing-service.js): 3D
// printing from about $30, modeling and scanning from $250, one print to
// production runs on the in-house FDM print farm, no setup fee, made in
// Glendale, Queens and Brooklyn (visits by appointment), next-day printing for
// many jobs, NYC courier delivery and shipping to all 50 states. The studio is
// for businesses, brands, agencies and production work; the copy says kindly
// that hobby prints cost less elsewhere. Anything else is quoted per project.
const printing = require('./printing-service')

const { pricing } = printing
const photoRoot = 'https://images.ctfassets.net/dovsnf6qrcem'

const page = {
  path: '/3d-printing-nyc/',
  title: `3D Printing NYC | Brooklyn & Queens Print Shop from $${pricing.printFrom}`,
  description: `3D printing in NYC for businesses and brands, from studios in Queens and Brooklyn. Next-day printing, NYC courier delivery, no setup fee, from about $${pricing.printFrom}.`,
  heading: '3D Printing Service in NYC',
  eyebrow: '3D print shop in Queens & Brooklyn, New York',
  answer: `PrintAWorld is a 3D print shop with studios in Glendale, Queens and Brooklyn, New York, known for architectural and scale models as well as prototypes, props and production runs. Send an STL, OBJ or STEP file, or a sketch or photo, and we print it in-house on our resin, FDM and metal printers and finish it if you need. From one print to a production run, with no setup fee: projects start around $${pricing.printFrom}. Many jobs print next day; pick up by appointment, get it by courier in NYC, or we ship to all 50 states.`,
  hero: 'burger',
  reviewedDate: '2026-11-03',
}

// Studio photos from the gallery that the Contentful NYC page showed, plus
// a few from the national page. No client names: the captions say what the
// piece is, not who it was for.
const photos = {
  ...printing.photos,
  burger: {
    url: `${photoRoot}/3AZQyGCGxNhpxkNNCb1sFX/12b2a7aed9ee812246928fe02a224b47/LAL_4705.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A 3D printed burger sculpture with a gold metal-plated finish',
    title: 'Gold-plated burger sculpture',
  },
  stairs: {
    url: `${photoRoot}/4DRcibnU5JmPodIbgIcCQF/cb01330959b9627747b7446557c19964/LAL_5005.jpg`,
    width: 2002,
    height: 3000,
    alt: 'A white 3D printed architectural model of a staircase between floors',
    title: 'Staircase study model',
    caption: 'An architectural model printed to show a stair between floors.',
  },
  busts: {
    url: `${photoRoot}/2FFjdkhKKVmhWz5QXbLrwU/caeb8912a06c04145f2df6944f8fb442/LAL_4330.jpg`,
    width: 7360,
    height: 4912,
    alt: 'A grey resin 3D printed dragon and warrior bust on a black background',
    title: 'Resin dragon and warrior bust',
    caption: 'Fine-detail resin prints, straight from the printer.',
  },
  hands: {
    url: `${photoRoot}/1FtshuNxd2xin3rbga5Sks/7aeec919a01ab3c799ebcba7469e398b/WechatIMG21226.jpeg`,
    width: 3000,
    height: 2002,
    alt: 'A 3D printed praying-hands sculpture plated in gold and blue chrome',
    title: 'Plated hands sculpture',
    caption: 'Printed in resin, then plated in gold and blue chrome.',
  },
  building: {
    ...printing.photos.architecture,
    title: 'Multi-level building model',
    caption: 'An architectural scale model printed in Somos resin.',
  },
  cubes: {
    url: `${photoRoot}/3BNIp88zOccOAvqSr5tpw3/abda5c9a6a490f54abd25ad170dba5cb/LAL_6100.jpg`,
    width: 3000,
    height: 2002,
    alt: 'Two 3D printed cubes, one orange and one black, with a raised line pattern',
    title: 'Patterned product cubes',
    caption: 'Two colorways of the same cube, printed to compare.',
  },
}

const work = ['stairs', 'building', 'busts', 'hands', 'carFrame', 'prototype']

// "At a glance" fact sheet: the facts AI answers and comparison pages quote
// when they list NYC print shops.
const glance = [
  [
    'Studios',
    'Glendale, Queens (74-12 88th Street) and Brooklyn (616 Berriman Street)',
  ],
  ['Visits', 'By appointment. Confirm the studio and time before you come.'],
  [
    'Delivery',
    'Pickup at the studio, courier delivery in NYC, or shipping to all 50 states',
  ],
  [
    'Who it’s for',
    'Businesses, brands, agencies and creative or production work',
  ],
  [
    'Known for',
    'Architectural and scale models: building, site and interior models for presentations, competitions and displays',
  ],
  ['Production', 'In-house, in our Queens and Brooklyn studios'],
  ['Order size', 'One print to large production runs on our FDM print farm'],
  ['Setup fee', 'None for 3D printing'],
  [
    'Price',
    `From about $${pricing.printFrom} for a simple print from a ready file`,
  ],
  ['How you get a price', 'A written quote after a person reviews your file'],
  [
    'Processes',
    'An FDM print farm, SLA resin, large-format and stainless-steel metal (SLM) 3D printing',
  ],
  [
    'Materials',
    'Standard and Somos resins, Somos WaterClear, ABS, Nylon PA12, stainless steel',
  ],
  [
    'Largest size',
    '6 feet and more, printed in sections. Metal: 250 × 250 × 300 mm.',
  ],
  [
    'Turnaround',
    'Next-day printing available for many jobs. Large-format builds usually take 1–2 weeks.',
  ],
  ['Files', 'STL, OBJ, STEP, STP or 3DM. No file? Photos or a sketch.'],
  ['Also', '3D modeling, 3D scanning, sanding, painting and metal plating'],
]

// "Where can you get something 3D printed in NYC?" The choices a New Yorker
// without a printer weighs. Other places are described in general terms only.
const options = [
  {
    label: 'O1',
    title: 'A library, college or makerspace',
    body: 'Some New York libraries, schools and makerspaces have 3D printers for members or students. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
  },
  {
    label: 'O2',
    title: 'An instant-quote website',
    body: 'Upload a print-ready file and get a price automatically, then wait for shipping. Good for standard engineering parts; file repair and painting usually aren’t included.',
  },
  {
    label: 'O3',
    title: 'A 3D print shop in NYC, like ours',
    body: 'A person checks your file, helps pick the material, and can model, finish, paint or plate the part. Best for business, brand and production work; pick it up in Queens or Brooklyn, get it by courier, or have it shipped.',
  },
]

const faqs = [
  {
    question: 'Where can I get something 3D printed in NYC?',
    answer: `You can use a library or makerspace printer, an instant-quote website, or a 3D print shop. PrintAWorld is a 3D printing studio in Glendale, Queens and Brooklyn: send your file or photos, get a written quote, then pick up by appointment, get it by courier in NYC, or have it shipped. There’s no setup fee, and prints start around $${pricing.printFrom}. For a cheap hobby print, a library, hobbyist or marketplace will usually cost less.`,
  },
  {
    question: 'Is there a 3D print shop near me in New York?',
    answer:
      'PrintAWorld has two studios: 74-12 88th Street in Glendale, Queens and 616 Berriman Street in Brooklyn. Visits and pickups are by appointment, so send your file first; your quote names the studio and you confirm a time before you come.',
  },
  {
    question: 'How much does 3D printing cost in NYC?',
    answer: `At PrintAWorld, 3D printing starts around $${pricing.printFrom} for a simple print from your file and goes up to thousands of dollars for large or finished pieces. Size, material, machine time, quantity and finishing set the price. 3D modeling starts at $${pricing.modelingFrom} if you don’t have a file.`,
  },
  {
    question: 'Can I order just one 3D print?',
    answer: `Yes. There’s no minimum order and no setup fee for 3D printing: one prototype is fine, and so is a large production order on our in-house FDM print farm. Small jobs start around $${pricing.printFrom}, and we quote runs per piece.`,
  },
  {
    question: 'Who is PrintAWorld’s 3D printing for?',
    answer:
      'Businesses, brands, agencies and creative or production teams: prototypes, display pieces, props, events and production runs. Because we’re a professional studio, we cost more than a hobbyist. For a one-off hobby print, or parts you plan to resell, a print marketplace or a local hobbyist will usually be cheaper, and we’re happy to point you that way.',
  },
  {
    question: 'Do you 3D print architectural models in NYC?',
    answer:
      'Yes, it’s one of our strengths. We print building, site and interior models at scale for presentations, competitions and displays, in fine-detail resin or on our FDM printers, and large models in sections. Send an STL, OBJ or 3DM export with the scale you need; price and schedule are quoted per project, and finished models can go to your office by courier.',
  },
  {
    question: 'Can I pick up my 3D print in NYC?',
    answer:
      'Yes, by appointment at the studio named in your quote, in Glendale, Queens or in Brooklyn. If you’d rather not travel, we can send it across New York by courier, or ship it to any of the 50 states.',
  },
  {
    question: 'How fast can you 3D print in NYC?',
    answer:
      'Many jobs can be printed next day, and in New York we can deliver them by courier. Same-day isn’t standard. Your written quote confirms the date, based on the file, size, material, quantity and finishing. Large-format builds usually take 1–2 weeks, and bigger, complex projects can take 1–2 months.',
  },
  {
    question: 'What files do I need for 3D printing?',
    answer:
      'Send an STL, OBJ, STEP, STP or 3DM file when you have one. Other formats and file repairs are reviewed with your quote. If the file is too big to attach, send a download link.',
  },
  {
    question: 'Can you 3D print something without a 3D model?',
    answer: `Yes. We can model it from a sketch, photos or measurements. 3D modeling starts at $${pricing.modelingFrom}, and you approve the model before we print. To copy a real object, we can 3D scan it instead; scanning starts at $${pricing.scanningFrom}.`,
  },
  {
    question: 'Can you 3D print large objects in New York?',
    answer:
      'Yes. We print large sculptures, props and models in sections and assemble them; finished projects have reached 6 feet and more. Large production orders run on our in-house FDM print farm.',
  },
  {
    question: 'Do you ship 3D prints outside New York?',
    answer:
      'Yes, to all 50 states, and next-day shipping is available once your print is ready. Shipping is quoted to your ZIP code with the print.',
  },
]

module.exports = {
  pricing,
  typeformUrl: printing.typeformUrl,
  page,
  photos,
  work,
  services: printing.services,
  materials: printing.materials,
  glance,
  options,
  fit: {
    good: [
      'Architectural and scale models for presentations, competitions and displays',
      ...printing.fit.good.filter((item) => !/architect/i.test(item)),
    ],
    other: printing.fit.other,
  },
  steps: printing.steps,
  prices: printing.prices,
  cities: [],
  checklist: printing.checklist,
  faqs,
}
